import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Loader2, Copy, Check, Send, RefreshCw, Linkedin, FileText, Settings, ChevronDown, ChevronUp, Pencil, Save } from 'lucide-react';
import { Toaster, toast } from '../ui/sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const STYLE_COLORS = {
  insight: 'bg-blue-50 border-blue-200 text-blue-700',
  listicle: 'bg-purple-50 border-purple-200 text-purple-700',
  story: 'bg-amber-50 border-amber-200 text-amber-700',
  question: 'bg-emerald-50 border-emerald-200 text-emerald-700',
};

const PostCard = ({ post, onApprove }) => {
  const [copied, setCopied] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [approving, setApproving] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editContent, setEditContent] = useState(post.content);
  const [saving, setSaving] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(editing ? editContent : post.content);
    setCopied(true);
    toast.success('Copied to clipboard!');
    setTimeout(() => setCopied(false), 2000);
  };

  const handleApprove = async (platform) => {
    setApproving(true);
    try {
      const res = await fetch(`${API}/api/admin/linkedin/approve`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ post_id: post.post_id, platform }),
      });
      const data = await res.json();
      toast.success(data.message);
      if (onApprove) onApprove();
    } catch {
      toast.error('Failed to approve post');
    }
    setApproving(false);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/admin/linkedin/update`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ post_id: post.post_id, content: editContent }),
      });
      if (res.ok) {
        post.content = editContent;
        setEditing(false);
        toast.success('Post updated!');
        if (onApprove) onApprove();
      } else {
        toast.error('Failed to save');
      }
    } catch {
      toast.error('Save failed');
    }
    setSaving(false);
  };

  const handleEdit = () => {
    setEditContent(post.content);
    setEditing(true);
    setExpanded(true);
  };

  const preview = post.content.slice(0, 180) + (post.content.length > 180 ? '...' : '');
  const styleClass = STYLE_COLORS[post.style] || 'bg-gray-50 border-gray-200 text-gray-700';

  return (
    <div data-testid={`linkedin-post-${post.post_id}`} className="border border-gray-200 rounded-lg overflow-hidden">
      <div className="p-4">
        <div className="flex items-center justify-between mb-2">
          <span className={`text-xs font-medium px-2 py-0.5 rounded-full border ${styleClass}`}>{post.label}</span>
          <span className={`text-xs px-2 py-0.5 rounded-full ${
            post.status === 'posted' ? 'bg-green-100 text-green-700' :
            post.status === 'approved' ? 'bg-blue-100 text-blue-700' :
            'bg-gray-100 text-gray-600'
          }`}>{post.status}</span>
        </div>
        {editing ? (
          <textarea
            data-testid={`edit-textarea-${post.post_id}`}
            value={editContent}
            onChange={e => setEditContent(e.target.value)}
            className="w-full text-sm text-gray-800 font-sans leading-relaxed mt-2 p-3 border border-gray-300 rounded-lg resize-y min-h-[200px] focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
            rows={12}
          />
        ) : (
          <>
            <pre className="whitespace-pre-wrap text-sm text-gray-800 font-sans leading-relaxed mt-2">
              {expanded ? post.content : preview}
            </pre>
            {post.content.length > 180 && (
              <button onClick={() => setExpanded(!expanded)} className="text-xs text-emerald-600 hover:underline mt-1 flex items-center gap-1">
                {expanded ? <><ChevronUp className="w-3 h-3" /> Show less</> : <><ChevronDown className="w-3 h-3" /> Show more</>}
              </button>
            )}
          </>
        )}
      </div>
      <div className="border-t border-gray-100 px-4 py-2.5 bg-gray-50 flex items-center gap-2 flex-wrap">
        {editing ? (
          <>
            <Button size="sm" onClick={handleSave} disabled={saving} className="text-xs h-8 bg-emerald-600 hover:bg-emerald-700 text-white" data-testid={`save-btn-${post.post_id}`}>
              {saving ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Save className="w-3.5 h-3.5 mr-1" />}
              Save
            </Button>
            <Button size="sm" variant="outline" onClick={() => setEditing(false)} className="text-xs h-8">Cancel</Button>
          </>
        ) : (
          <>
            <Button size="sm" variant="outline" onClick={handleEdit} className="text-xs h-8" data-testid={`edit-btn-${post.post_id}`}>
              <Pencil className="w-3.5 h-3.5 mr-1" /> Edit
            </Button>
            <Button size="sm" variant="outline" onClick={handleCopy} className="text-xs h-8" data-testid={`copy-btn-${post.post_id}`}>
              {copied ? <Check className="w-3.5 h-3.5 mr-1" /> : <Copy className="w-3.5 h-3.5 mr-1" />}
              {copied ? 'Copied' : 'Copy'}
            </Button>
            {post.status === 'pending' && (
              <>
                <Button size="sm" onClick={() => handleApprove('zapier')} disabled={approving} className="text-xs h-8 bg-orange-500 hover:bg-orange-600 text-white" data-testid={`zapier-btn-${post.post_id}`}>
                  {approving ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Send className="w-3.5 h-3.5 mr-1" />}
                  Zapier
                </Button>
                <Button size="sm" onClick={() => handleApprove('zoho')} disabled={approving} className="text-xs h-8 bg-red-500 hover:bg-red-600 text-white" data-testid={`zoho-btn-${post.post_id}`}>
                  {approving ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Send className="w-3.5 h-3.5 mr-1" />}
                  Zoho
                </Button>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
};

const BlogRow = ({ blog, onGenerate }) => {
  const [posts, setPosts] = useState([]);
  const [expanded, setExpanded] = useState(false);
  const [generating, setGenerating] = useState(false);

  const generate = async () => {
    setGenerating(true);
    try {
      const res = await fetch(`${API}/api/admin/linkedin/generate/${blog.blogId}`, { method: 'POST' });
      const data = await res.json();
      setPosts(data.posts || []);
      setExpanded(true);
      toast.success(`Generated ${data.posts?.length || 0} posts`);
      if (onGenerate) onGenerate();
    } catch {
      toast.error('Generation failed');
    }
    setGenerating(false);
  };

  const loadPosts = async () => {
    const res = await fetch(`${API}/api/admin/linkedin/posts?status=pending`);
    const data = await res.json();
    const blogPosts = (data.posts || []).filter(p => p.blog_id === blog.blogId);
    setPosts(blogPosts);
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadPosts(); }, [blog.blogId]);

  return (
    <div className="border border-gray-200 rounded-xl overflow-hidden">
      <div className="p-4 flex items-center justify-between gap-4 cursor-pointer hover:bg-gray-50" onClick={() => setExpanded(!expanded)}>
        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-semibold text-gray-900 truncate">{blog.title}</h3>
          <p className="text-xs text-gray-500 mt-0.5">{blog.category} · {posts.length} posts generated</p>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <Button size="sm" onClick={(e) => { e.stopPropagation(); generate(); }} disabled={generating} className="text-xs h-8 bg-emerald-600 hover:bg-emerald-700 text-white" data-testid={`generate-btn-${blog.blogId}`}>
            {generating ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5 mr-1" />}
            Generate
          </Button>
          {expanded ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
        </div>
      </div>
      {expanded && posts.length > 0 && (
        <div className="border-t border-gray-100 p-4 space-y-3 bg-gray-50/50">
          {posts.map(p => <PostCard key={p.post_id} post={p} onApprove={loadPosts} />)}
        </div>
      )}
      {expanded && posts.length === 0 && (
        <div className="border-t border-gray-100 p-6 text-center text-sm text-gray-500">
          No posts generated yet. Click "Generate" to create LinkedIn posts.
        </div>
      )}
    </div>
  );
};

export const LinkedInDashboard = () => {
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [bulkGenerating, setBulkGenerating] = useState(false);
  const [showConfig, setShowConfig] = useState(false);
  const [zapierUrl, setZapierUrl] = useState('');
  const [zohoUrl, setZohoUrl] = useState('');
  const [webhookStatus, setWebhookStatus] = useState({ zapier: false, zoho: false });
  const [stats, setStats] = useState({ total: 0, pending: 0, posted: 0 });

  const loadBlogs = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/blogs/?status=all&limit=100`);
      const data = await res.json();
      setBlogs(data.blogs || []);
    } catch {
      toast.error('Failed to load blogs');
    }
    setLoading(false);
  }, []);

  const loadStats = useCallback(async () => {
    try {
      const [pending, posted, config] = await Promise.all([
        fetch(`${API}/api/admin/linkedin/posts?status=pending`).then(r => r.json()),
        fetch(`${API}/api/admin/linkedin/posts?status=posted`).then(r => r.json()),
        fetch(`${API}/api/admin/linkedin/webhook-config`).then(r => r.json()),
      ]);
      setStats({ total: (pending.total || 0) + (posted.total || 0), pending: pending.total || 0, posted: posted.total || 0 });
      setWebhookStatus({ zapier: config.zapier, zoho: config.zoho });
    } catch (err) { console.error('Failed to load stats:', err); }
  }, []);

  useEffect(() => { loadBlogs(); loadStats(); }, [loadBlogs, loadStats]);

  const handleBulkGenerate = async () => {
    setBulkGenerating(true);
    try {
      const res = await fetch(`${API}/api/admin/linkedin/generate-all`, { method: 'POST' });
      const data = await res.json();
      toast.success(data.message);
      loadStats();
    } catch {
      toast.error('Bulk generation failed');
    }
    setBulkGenerating(false);
  };

  const handleSaveWebhook = async () => {
    try {
      const res = await fetch(`${API}/api/admin/linkedin/webhook-config`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ zapier_url: zapierUrl || null, zoho_url: zohoUrl || null }),
      });
      const data = await res.json();
      toast.success(data.message);
      setWebhookStatus({ zapier: data.zapier, zoho: data.zoho });
      setShowConfig(false);
    } catch {
      toast.error('Failed to save webhook config');
    }
  };

  return (
    <div data-testid="linkedin-dashboard" className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center">
            <Linkedin className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-gray-900">LinkedIn Content Studio</h1>
            <p className="text-sm text-gray-500">Generate, approve, and post blog content to LinkedIn</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" onClick={() => setShowConfig(!showConfig)} className="text-xs h-8" data-testid="webhook-config-btn">
            <Settings className="w-3.5 h-3.5 mr-1" /> Webhooks
            {(webhookStatus.zapier || webhookStatus.zoho) && <span className="ml-1 w-2 h-2 bg-green-500 rounded-full"></span>}
          </Button>
          <Button size="sm" onClick={handleBulkGenerate} disabled={bulkGenerating} className="text-xs h-8 bg-blue-600 hover:bg-blue-700 text-white" data-testid="bulk-generate-btn">
            {bulkGenerating ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <FileText className="w-3.5 h-3.5 mr-1" />}
            Generate All
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-white border border-gray-200 rounded-lg p-4 text-center">
          <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
          <p className="text-xs text-gray-500">Total Posts</p>
        </div>
        <div className="bg-white border border-gray-200 rounded-lg p-4 text-center">
          <p className="text-2xl font-bold text-amber-600">{stats.pending}</p>
          <p className="text-xs text-gray-500">Pending Approval</p>
        </div>
        <div className="bg-white border border-gray-200 rounded-lg p-4 text-center">
          <p className="text-2xl font-bold text-green-600">{stats.posted}</p>
          <p className="text-xs text-gray-500">Posted</p>
        </div>
      </div>

      {/* Webhook Config */}
      {showConfig && (
        <div className="bg-white border border-gray-200 rounded-xl p-4 space-y-3" data-testid="webhook-config-panel">
          <h3 className="text-sm font-semibold text-gray-900">Automation Webhooks</h3>
          <p className="text-xs text-gray-500">Connect Zapier or Zoho Flow to auto-post approved content to LinkedIn.</p>
          <div className="space-y-2">
            <div>
              <label className="text-xs font-medium text-gray-700 mb-1 block">Zapier Webhook URL</label>
              <Input data-testid="zapier-url-input" value={zapierUrl} onChange={e => setZapierUrl(e.target.value)} placeholder="https://hooks.zapier.com/hooks/catch/..." className="text-xs h-8" />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-700 mb-1 block">Zoho Flow Webhook URL</label>
              <Input data-testid="zoho-url-input" value={zohoUrl} onChange={e => setZohoUrl(e.target.value)} placeholder="https://flow.zoho.com/..." className="text-xs h-8" />
            </div>
          </div>
          <Button size="sm" onClick={handleSaveWebhook} className="text-xs h-8 bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="save-webhook-btn">Save Configuration</Button>
        </div>
      )}

      {/* How It Works */}
      <div className="bg-blue-50 border border-blue-100 rounded-xl p-4">
        <h3 className="text-sm font-semibold text-blue-900 mb-2">How LinkedIn Automation Works</h3>
        <ol className="text-xs text-blue-800 space-y-1 list-decimal list-inside">
          <li><strong>Generate</strong> — Create 4 post variations per blog (Insight, Listicle, Story, Question)</li>
          <li><strong>Review & Copy</strong> — Preview each post, copy to clipboard, or edit before posting</li>
          <li><strong>Approve & Post</strong> — Click Zapier/Zoho to auto-send via webhook, or copy-paste manually</li>
        </ol>
        <p className="text-xs text-blue-700 mt-2">
          <strong>Setup:</strong> Create a Zap in Zapier (Webhook → LinkedIn) or a Flow in Zoho (Webhook → LinkedIn Post). Paste the webhook URL above.
        </p>
      </div>

      {/* Blog List */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
        </div>
      ) : (
        <div className="space-y-3">
          {blogs.map(blog => (
            <BlogRow key={blog.blogId} blog={blog} onGenerate={loadStats} />
          ))}
        </div>
      )}

      <Toaster />
    </div>
  );
};
